import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FieldTransformerService } from '../dynamic-entity-form-component/services/field-transformer.service';
import { ActivatedRoute, Router } from '@angular/router';
import { AgGridToolsService } from 'src/app/common/ag-grid-table/services/ag-grid-tools.service';
import { ColDef } from 'ag-grid-community';
import { getDefaultTableColumnsForDynamicEntity } from 'src/app/common/ag-grid-table/default-table-columns';
import { RefsCellRendererComponent } from 'src/app/common/ag-grid-table/cell-renderers/render-components/refs/refs.component';
import { DropdownEditorComponent } from 'src/app/common/ag-grid-table/cell-editors/dropdown/dropdown.component';
import { CommonEntityService } from 'src/app/common/services/common-entity.service';
import { OptionsParams, UtilitiesService } from 'src/app/common/services/utilities.service';
import { TableCommunicationService } from 'src/app/common/ag-grid-table/services/table-communication.service';
import { MultiselectEditorComponent } from 'src/app/common/ag-grid-table/cell-editors/multiselect/multiselect.component';
import { TextCellRendererComponent } from 'src/app/common/ag-grid-table/cell-renderers/render-components/text/text.component';
import { LoggerService } from 'src/app/common/services/logger.service';

@Component({
  selector: 'app-dynamic-entity-table',
  templateUrl: './dynamic-entity-table.component.html',
  styleUrls: ['./dynamic-entity-table.component.sass']
})
export class DynamicEntityTableComponent implements OnInit {
  
  public columnDefs: ColDef[] = [];
  public entity: string;
  public title: string;
  public viewRoute: string;
  isLoading: boolean = true;
  public columnSelection: string;
  public modelEntity: string;
  public options: any = {};
  public tableParams: any = {};
  public bulkUpdateColDefs: any[] = [];
  
  constructor(
    private fieldTransformer: FieldTransformerService,
    private route: ActivatedRoute,
    private agGridToolsService: AgGridToolsService,
    private router: Router,
    private commonEntityService: CommonEntityService,
    private utilitiesService: UtilitiesService,
    private changeDetectionRef: ChangeDetectorRef,
    private tableCommunicationService: TableCommunicationService,
    private logger: LoggerService
  ) {}

  async ngOnInit() {
    this.logger.log('Initializing DynamicEntityTableComponent');
    
    this.route.params.subscribe(params => {
      this.entity = params['entityName'];
      this.logger.log('Entity from URL:', this.entity);
    });

    try {
      this.logger.log('Fetching metadata fields for entity:', this.entity);
      const metadataFields = await this.commonEntityService.getMetadataFields(this.entity);

      this.logger.log('Received metadata fields:', metadataFields);

      const hasName = metadataFields.some((field: any) => field.name === 'name');
      const hasStart = metadataFields.some((field: any) => field.name === 'start');
      const hasEnd = metadataFields.some((field: any) => field.name === 'end');

      if (metadataFields) {
        const defaultColumns = getDefaultTableColumnsForDynamicEntity(this.entity, this.agGridToolsService, { isDynamicEntity: true, hasName, hasStart, hasEnd }, {
          isLink: true,
          cellRendererParams: {
            isDynamicEntityWithId: true,
          }
        });
        
        const dynamicColumns = metadataFields.filter((field: any) => field.name !== 'name' && field.name !== 'start' && field.name !== 'end').map((field: any) => {
          if (field.apiModelName) {
            return {
              headerName: field.label || (field.name.charAt(0).toUpperCase() + field.name.slice(1).replace(/_/g, ' ')),
              field: field.name,
              cellRenderer: RefsCellRendererComponent,
              cellRendererParams: {
                entity: field.isDynamicEntity ? field.apiModelName : field.apiController,
                linkFormatter: field.type !== 'Array' ? (params: any) => {
                  if (field.isDynamicEntity) {
                    return `d/${params.entity}/view/${params.value.id}`;
                  }
                  return `${params.entity}/${params.value.id}`;
                } : undefined,
                optionLabel: field.type !== 'Array' ? field.optionLabel : undefined,
                isArray: field.type === 'Array',
                isDynamicEntity: field.isDynamicEntity,
              },
              sortable: true,
              unSortIcon: true,
              resizable: true,
              filter: 'agSetColumnFilter',
              filterParams: {
                values: [], // It'll be populated with the function updateFilterColumnDefs and even it could be removed from here
                valueFormatter: (params: any) => {
                  return `${params.value.name || params.value.description || params.value.title} (${params.value.id})`;
                },
                keyCreator: (params: any) => {
                  return params.value._id;
                },
                comparator: this.agGridToolsService.nameASC,
                isSingleRefFilter: true,
                addEmptyFilters: true,
                isMultiRefFilter: field.type === 'Array',
                populate: true,
                select: field.select
              } ,
              floatingFilter: true,
              cellEditor: field.type === 'Array' ? MultiselectEditorComponent : DropdownEditorComponent,
              cellEditorPopup: field.type === 'Array',
              cellEditorParams: {
                optionValue: field.optionValue || "_id",
                optionLabel: field.optionLabel || "name",
                objectValue: true,
              },
              type: 'editableColumn',
            };
          }

          // Check if this is a color field
          if (field.type === 'String' && field.subType === 'color') {
            return {
              headerName: field.label || (field.name.charAt(0).toUpperCase() + field.name.slice(1).replace(/_/g, ' ')),
              field: field.name,
              cellRenderer: TextCellRendererComponent,
              cellRendererParams: {
                isColorPicker: true
              },
              sortable: true,
              unSortIcon: true,
              resizable: true,
              filter: 'agTextColumnFilter',
              floatingFilter: true,
              autoHeight: true,
              cellEditor: 'agTextCellEditor',
              type: 'editableColumn'
            };
          }

          return {
            headerName: field.label || (field.name.charAt(0).toUpperCase() + field.name.slice(1).replace(/_/g, ' ')),
            field: field.name,
            sortable: true,
            unSortIcon: true,
            resizable: true,
            filter: 'agTextColumnFilter',
            floatingFilter: true,
            autoHeight: true,
            cellEditor: 'agTextCellEditor',
            type: 'editableColumn'
          };
        });
        this.logger.log('Dynamic columns:', dynamicColumns);

        const uniqueColumns = [...defaultColumns];
        dynamicColumns.forEach((dynCol: any) => {
          if (!uniqueColumns.some(col => col.field === dynCol.field)) {
            uniqueColumns.push(dynCol);
          }
        });

        this.columnDefs = uniqueColumns;
        this.logger.log('Final unique column definitions:', this.columnDefs);

        this.columnSelection = this.agGridToolsService.getColumnFieldNamesFromCols(this.columnDefs);
        this.logger.log('Column selection:', this.columnSelection);
      }

      const metadataFieldsAsObjectId = metadataFields.filter((field: any) => field.apiModelName);
      this.logger.log('Get metadata fields of ObjectId type:', metadataFieldsAsObjectId);

      if (metadataFieldsAsObjectId.length > 0) {
        this.setOptions(metadataFieldsAsObjectId);
      }

      this.title = `${this.entity} Table`;
      this.viewRoute = this.entity;
      this.isLoading = false;

      this.tableParams = {
        customActionsLabel: 'Custom Bulk Actions',
        customActionButtons: this.entity === 'questions'
          ? ['bulkLocalize', 'importQuestions']
          : this.entity === 'answers'
            ? ['bulkLocalize', 'importAnswers']
            : ['bulkLocalize'],
        localizeCollectionName: this.entity,
        isDynamicEntity: true,
      }

      if (this.entity === 'questions') {
        const miniGameTypes = await this.utilitiesService.getOptions(this.options, '_miniGameTypesBulk', new OptionsParams({
          entity: 'minigame-types',
          select: 'name id value _id',
          sort: { name: 1 },
        }));
        this.bulkUpdateColDefs = [
          { name: 'Question', id: 'question', type: 'text' },
          { name: 'Mini Game Type', id: 'minigame_type', type: 'dropdown', optionLabel: 'name', optionValue: '_id', smartPopulate: true, options: miniGameTypes },
        ];
      }
    } catch (error) {
      console.error('Error in DynamicEntityTableComponent:', error);
      this.isLoading = false;
    }
  }

  async setOptions(fields: any[]) {
    const entities = fields.map((field) => ({
      ref: field.name,
      entity: field.isDynamicEntity ? field.apiModelName : field.apiController,
      select: field.select,
      optionLabel: field.optionLabel,
      isDynamicEntity: field.isDynamicEntity
    }));
    this.logger.log('Get entities options:', entities);

    Promise.all(entities.map(async ({ ref, entity, select, query, isDynamicEntity }: any) => {
      const params = new OptionsParams({
        entity, select, query, dynamicOptions: { isDynamicEntity }
      });
      if (!isDynamicEntity) {
        delete params.dynamicOptions;
      }
      return await this.utilitiesService.getOptions(this.options, ref, params);
    })).then(() => {
      this.updateFilterColumnDefs(entities);

      // Share the line item options via the communication service
      const lineItemOptions = {
        resources: this.options.resources_ref,
        currencies: this.options.currencies_ref,
        titles: this.options.titles_ref
      };
      this.tableCommunicationService.setLineItemOptions(lineItemOptions);
      // Emit the options update to notify cell renderers
      this.tableCommunicationService.emitOptionsUpdate(this.options);
    });
  }

  updateFilterColumnDefs(entities: any[]) {
    this.columnDefs = this.columnDefs.map((col) => {
      const entity = entities.find((entity) => entity.ref === col.field);
      if (entity && col.field) {
        // Handle complex filters (like costs_ref that has multiple filters)
        if (col.filterParams?.filters) {
          const updatedFilters = col.filterParams.filters.map((filter: any) => {
            if (filter.filter === 'agSetColumnFilter') {
              // Update the values for the currency filter
              return {
                ...filter,
                filterParams: {
                  ...filter.filterParams,
                  values: this.options.currencies_ref || [],
                }
              };
            }
            return filter;
          });

          return {
            ...col,
            filterParams: {
              ...col.filterParams,
              filters: updatedFilters,
            },
          };
        }

        const filterParams = {
          ...col.filterParams,
          values: this.options[col.field] || [],
        };

        // Add empty/non-empty options similar to preprocessColumnDefs
        if (col.filter === 'agSetColumnFilter' && col.filterParams?.addEmptyFilters) {
          if (col.filterParams.isMultiRefFilter || col.filterParams.isSingleRefFilter) {
            // Handle multi-reference filters by adding objects
            const values = filterParams.values as any[] || [];
            if (!values.some(value => value.name === '(EMPTY)')) {
              values.push({ [entity.optionLabel]: '(EMPTY)', id: -1, _id: -1 });
            }
            if (!values.some(value => value.name === '(NOT EMPTY)')) {
              values.push({ [entity.optionLabel]: '(NOT EMPTY)', id: -2, _id: -2 });
            }
            filterParams.values = values;
          } else {
            // Handle simple filters by adding string values
            const values = filterParams.values as string[] || [];
            if (!values.includes('(EMPTY)')) values.push('(EMPTY)');
            if (!values.includes('(NOT EMPTY)')) values.push('(NOT EMPTY)');
            filterParams.values = values;
          }
        }

        return {
          ...col,
          filterParams,
        };
      }
      return col;
    });

    this.changeDetectionRef.detectChanges();
    this.logger.log('====== Challenge filters updated =====');
  }

}
